<template>
  <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
    <!-- Compact Header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center space-x-3">
        <div
          class="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center"
        >
          <i class="material-icons text-white text-lg">public</i>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900">
            Global Distribution
          </h3>
          <p class="text-sm text-gray-500">
            {{ totalCountries }} markets • {{ totalTenants }} tenants
          </p>
        </div>
      </div>
      <div class="flex items-center space-x-2">
        <span
          class="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium"
        >
          {{ concentrationLevel }}
        </span>
      </div>
    </div>

    <!-- World Map Visualization -->
    <div class="mb-6">
      <div
        class="relative bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6 overflow-hidden"
      >
        <!-- Decorative world map background -->
        <div class="absolute inset-0 opacity-10">
          <svg viewBox="0 0 400 200" class="w-full h-full">
            <path
              d="M50,80 Q70,60 90,80 Q110,100 130,80 Q150,60 170,80 Q190,100 210,80 Q230,60 250,80 Q270,100 290,80 Q310,60 330,80 Q350,100 370,80"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            />
            <circle cx="80" cy="90" r="3" fill="currentColor" />
            <circle cx="120" cy="70" r="2" fill="currentColor" />
            <circle cx="180" cy="95" r="2" fill="currentColor" />
            <circle cx="240" cy="75" r="3" fill="currentColor" />
            <circle cx="300" cy="85" r="2" fill="currentColor" />
          </svg>
        </div>

        <!-- Market bubbles visualization -->
        <div class="relative z-10">
          <h4 class="text-sm font-medium text-gray-700 mb-4">
            Market Size Distribution
          </h4>
          <div class="flex items-end space-x-2 h-24">
            <div
              v-for="(country, index) in topMarkets"
              :key="country.name"
              class="flex flex-col items-center group cursor-pointer transition-all duration-300 hover:scale-105"
              @click="selectCountry(country)"
            >
              <!-- Market bubble -->
              <div
                class="rounded-full mb-2 flex items-center justify-center relative"
                :style="{
                  width: getBubbleSize(country) + 'px',
                  height: getBubbleSize(country) + 'px',
                  backgroundColor: getMarketColor(index),
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }"
              >
                <img
                  :src="country.flag"
                  :alt="country.name"
                  class="w-4 h-3 rounded-sm object-cover opacity-90"
                  @error="handleImageError"
                />
                <!-- Pulse animation for top markets -->
                <div
                  v-if="index < 3"
                  class="absolute inset-0 rounded-full animate-ping opacity-25"
                  :style="{ backgroundColor: getMarketColor(index) }"
                ></div>
              </div>

              <!-- Country label -->
              <div class="text-center">
                <div
                  class="text-xs font-medium text-gray-700 truncate max-w-16"
                >
                  {{ country.name.split(" ")[0] }}
                </div>
                <div class="text-xs text-gray-500">{{ country.tenants }}</div>
              </div>

              <!-- Tooltip -->
              <div
                class="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
              >
                <div
                  class="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap"
                >
                  <div class="font-medium">{{ country.name }}</div>
                  <div>
                    {{ country.tenants }} tenants ({{ country.percentage }}%)
                  </div>
                  <div class="text-gray-300">
                    {{ getMarketCategory(country) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Geographic Analytics Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <!-- Market Concentration Chart -->
      <div class="bg-gray-50 rounded-lg p-4">
        <h4 class="text-sm font-medium text-gray-700 mb-3">
          Market Concentration
        </h4>
        <div class="space-y-2">
          <div
            v-for="segment in concentrationSegments"
            :key="segment.label"
            class="flex items-center justify-between"
          >
            <div class="flex items-center space-x-2">
              <div
                class="w-3 h-3 rounded-full"
                :style="{ backgroundColor: segment.color }"
              ></div>
              <span class="text-xs text-gray-600">{{ segment.label }}</span>
            </div>
            <div class="flex items-center space-x-2">
              <div class="h-2 rounded-full bg-gray-200 w-16 overflow-hidden">
                <div
                  class="h-full rounded-full transition-all duration-500"
                  :style="{
                    width: segment.percentage + '%',
                    backgroundColor: segment.color
                  }"
                ></div>
              </div>
              <span class="text-xs font-medium text-gray-700 w-8 text-right">
                {{ segment.percentage }}%
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Regional Performance -->
      <div class="bg-gray-50 rounded-lg p-4">
        <h4 class="text-sm font-medium text-gray-700 mb-3">
          Regional Insights
        </h4>
        <div class="space-y-3">
          <div
            v-for="insight in regionalInsights"
            :key="insight.region"
            class="flex items-start space-x-2"
          >
            <div
              class="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
              :style="{ backgroundColor: insight.color }"
            ></div>
            <div>
              <div class="text-xs font-medium text-gray-700">
                {{ insight.region }}
              </div>
              <div class="text-xs text-gray-600">{{ insight.description }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Key Metrics Bar -->
    <div class="grid grid-cols-4 gap-4 mb-6">
      <div
        class="text-center p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg"
      >
        <div class="text-xl font-bold text-blue-700">
          {{ dominantCountry.name }}
        </div>
        <div class="text-xs text-blue-600">Leading Market</div>
        <div class="text-xs text-blue-500 mt-1">
          {{ dominantCountry.percentage }}%
        </div>
      </div>
      <div
        class="text-center p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-lg"
      >
        <div class="text-xl font-bold text-green-700">
          {{ marketConcentration }}%
        </div>
        <div class="text-xs text-green-600">Top Market</div>
        <div class="text-xs text-green-500 mt-1">{{ concentrationLevel }}</div>
      </div>
      <div
        class="text-center p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg"
      >
        <div class="text-xl font-bold text-purple-700">
          {{ diversityScore }}
        </div>
        <div class="text-xs text-purple-600">Diversity</div>
        <div class="text-xs text-purple-500 mt-1">
          {{ totalCountries }} markets
        </div>
      </div>
      <div
        class="text-center p-3 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg"
      >
        <div class="text-xl font-bold text-orange-700">
          {{ growthPotential }}
        </div>
        <div class="text-xs text-orange-600">Growth Potential</div>
        <div class="text-xs text-orange-500 mt-1">
          {{ emergingMarkets }} emerging
        </div>
      </div>
    </div>

    <!-- Strategic Insights -->
    <div class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
      <div class="flex items-start space-x-3">
        <div
          class="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        >
          <i class="material-icons text-white text-xs">insights</i>
        </div>
        <div class="flex-1">
          <h4 class="text-sm font-medium text-gray-800 mb-2">
            Strategic Analysis
          </h4>
          <div class="space-y-2">
            <div
              v-for="insight in strategicInsights"
              :key="insight.type"
              class="flex items-start space-x-2"
            >
              <i
                class="material-icons text-xs mt-0.5"
                :class="insight.iconColor"
              >
                {{ insight.icon }}
              </i>
              <p class="text-xs text-gray-600 leading-relaxed">
                {{ insight.text }}
              </p>
            </div>
          </div>
          <div class="flex items-center justify-end mt-3">
            <button
              @click="
                $emit('view-details', { geographicalData: geographicalData })
              "
              class="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              View Detailed Report →
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GeographicalWidget",
  props: {
    geographicalData: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedCountry: null
    };
  },
  computed: {
    totalTenants() {
      return this.geographicalData.reduce(
        (sum, country) => sum + (country.tenants || 0),
        0
      );
    },

    totalCountries() {
      return this.geographicalData.length;
    },

    sortedCountries() {
      return [...this.geographicalData].sort(
        (a, b) => (b.tenants || 0) - (a.tenants || 0)
      );
    },

    topMarkets() {
      return this.sortedCountries.slice(0, 6); // Show top 6 for bubble chart
    },

    dominantCountry() {
      if (this.sortedCountries.length === 0) {
        return { name: "N/A", tenants: 0, percentage: 0 };
      }
      return this.sortedCountries[0];
    },

    marketConcentration() {
      return this.dominantCountry.percentage || 0;
    },

    concentrationLevel() {
      const concentration = this.marketConcentration;
      if (concentration >= 50) return "High";
      if (concentration >= 30) return "Medium";
      if (concentration >= 20) return "Balanced";
      return "Distributed";
    },

    diversityScore() {
      if (this.totalCountries <= 3) return "Low";
      if (this.totalCountries >= 15) return "High";
      if (this.totalCountries >= 8) return "Medium";
      return "Low";
    },

    growthPotential() {
      const smallMarkets = this.sortedCountries.filter(c => c.percentage < 5)
        .length;
      if (smallMarkets >= 10) return "High";
      if (smallMarkets >= 5) return "Medium";
      return "Low";
    },

    emergingMarkets() {
      return this.sortedCountries.filter(c => c.percentage < 2 && c.tenants > 0)
        .length;
    },

    concentrationSegments() {
      const top3 = this.sortedCountries.slice(0, 3);
      const others = this.sortedCountries.slice(3);
      const othersPercentage = others.reduce((sum, c) => sum + c.percentage, 0);

      const segments = [
        ...top3.map((country, index) => ({
          label: country.name,
          percentage: country.percentage,
          color: this.getMarketColor(index)
        }))
      ];

      if (othersPercentage > 0) {
        segments.push({
          label: `Other Markets (${others.length})`,
          percentage: othersPercentage.toFixed(1),
          color: "#9CA3AF"
        });
      }

      return segments;
    },

    regionalInsights() {
      const insights = [];
      const sorted = this.sortedCountries;

      if (sorted.length === 0) return insights;

      // Market dominance insight
      if (sorted[0]?.percentage >= 40) {
        insights.push({
          region: "Market Leadership",
          description: `${sorted[0].name} dominates with ${sorted[0].percentage}% market share`,
          color: "#3B82F6"
        });
      }

      // Geographic spread
      if (this.totalCountries >= 10) {
        insights.push({
          region: "Global Presence",
          description: `Strong international footprint across ${this.totalCountries} countries`,
          color: "#10B981"
        });
      } else if (this.totalCountries >= 5) {
        insights.push({
          region: "Regional Focus",
          description: `Established presence in ${this.totalCountries} key markets`,
          color: "#F59E0B"
        });
      }

      // Growth opportunities
      const smallMarkets = this.sortedCountries.filter(c => c.percentage < 3)
        .length;
      if (smallMarkets >= 5) {
        insights.push({
          region: "Expansion Potential",
          description: `${smallMarkets} emerging markets show growth opportunities`,
          color: "#8B5CF6"
        });
      }

      return insights;
    },

    strategicInsights() {
      const insights = [];
      const sorted = this.sortedCountries;

      if (sorted.length === 0) return insights;

      // Risk assessment
      if (sorted[0]?.percentage >= 50) {
        insights.push({
          type: "risk",
          icon: "warning",
          iconColor: "text-orange-500",
          text: `High concentration risk: ${sorted[0].name} represents ${sorted[0].percentage}% of tenant base. Consider diversification strategies.`
        });
      }

      // Growth opportunity
      if (this.emergingMarkets >= 3) {
        insights.push({
          type: "opportunity",
          icon: "trending_up",
          iconColor: "text-green-500",
          text: `Growth opportunity: ${this.emergingMarkets} emerging markets with early adoption. Focus investment for expansion.`
        });
      }

      // Market maturity
      if (sorted.length >= 10 && sorted[2]?.percentage >= 10) {
        insights.push({
          type: "stability",
          icon: "balance",
          iconColor: "text-blue-500",
          text: `Stable distribution: Top 3 markets (${sorted
            .slice(0, 3)
            .reduce((sum, c) => sum + c.percentage, 0)
            .toFixed(1)}%) provide balanced revenue foundation.`
        });
      }

      return insights;
    }
  },

  methods: {
    getBubbleSize(country) {
      const maxSize = 48;
      const minSize = 24;
      const maxTenants = Math.max(...this.sortedCountries.map(c => c.tenants));
      const ratio = country.tenants / maxTenants;
      return Math.round(minSize + (maxSize - minSize) * ratio);
    },

    getMarketColor(index) {
      const colors = [
        "#3B82F6", // Blue
        "#10B981", // Green
        "#F59E0B", // Yellow
        "#EF4444", // Red
        "#8B5CF6", // Purple
        "#EC4899" // Pink
      ];
      return colors[index % colors.length];
    },

    getMarketCategory(country) {
      if (country.percentage >= 20) return "Major Market";
      if (country.percentage >= 10) return "Key Market";
      if (country.percentage >= 5) return "Growing Market";
      return "Emerging Market";
    },

    selectCountry(country) {
      this.selectedCountry = country;
      this.$emit("country-selected", country);
    },

    handleImageError(event) {
      event.target.style.display = "none";
    }
  }
};
</script>

<style scoped>
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-ping {
  animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

@keyframes ping {
  75%,
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

/* Custom scrollbar styling */
::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
